import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Plus, Settings2, Trash2, X } from 'lucide-react';
import { api } from '../api/client';
import { useSocle } from '../hooks/useSocle';
import { fmtCHF, fmtPct, fmtMoisFr } from '../utils/format';
import { MOIS_LABELS, MOIS_LABELS_LONGS, Prestation, ScenarioBudget } from '../types';
import type {
  AgregatRH, EvenementRepartitionRH, FonctionRH, GrilleRH, LigneEmployeRH,
} from '../types/rh';
import { BadgeTagRevenu, ChoixTagScenario } from '../components/TagScenarioRevenu';
import { SelectAnnee } from '../components/SelectAnnee';

type Vue = 'personnes' | 'fonctions' | 'prestations';

/**
 * RH — répartition du temps de travail.
 *
 * Salaires répond à « combien coûte cette personne » ; RH répond à « à quoi va
 * son temps ». Chaque personne répartit son taux d'occupation entre les
 * fonctions, et les fonctions de terrain se ventilent par prestation — un même
 * animateur peut porter de l'atelier créatif et de l'atelier nature.
 *
 * D'où les deux lectures agrégées : ETP et coût salarial par fonction (lecture
 * métier) et par prestation (lecture analytique, à rapprocher du prix payé par
 * les contrats pour leur panier de prestations).
 *
 * L'écart entre la somme des lignes et le taux d'occupation est signalé, jamais
 * corrigé en silence.
 */
export function RHPage() {
  const maintenant = new Date();
  const [annee, setAnnee] = useState(maintenant.getFullYear());
  const [moisRef, setMoisRef] = useState(maintenant.getMonth() + 1);
  // undefined = socle pas encore connu ; la grille attend avant de charger
  const socleId = useSocle();
  const [scenarioId, setScenarioId] = useState<number | null | undefined>(undefined);
  const [vue, setVue] = useState<Vue>('personnes');
  const [grille, setGrille] = useState<GrilleRH | null>(null);
  const [fonctions, setFonctions] = useState<FonctionRH[]>([]);
  const [prestations, setPrestations] = useState<Prestation[]>([]);
  const [erreur, setErreur] = useState<string | null>(null);
  const [chargement, setChargement] = useState(true);
  const [fiche, setFiche] = useState<LigneEmployeRH | null>(null);

  const charger = useCallback(() => {
    if (scenarioId === undefined) return; // on attend de connaître le socle
    setErreur(null);
    api.get<GrilleRH>(`/rh/grille?annee=${annee}&mois=${moisRef}&scenario=${scenarioId ?? 'definitif'}`)
      .then(setGrille)
      .catch((e) => setErreur(e.message))
      .finally(() => setChargement(false));
  }, [annee, moisRef, scenarioId]);

  useEffect(() => {
    if (scenarioId === undefined && socleId !== undefined) setScenarioId(socleId);
  }, [socleId, scenarioId]);

  useEffect(() => { charger(); }, [charger]);

  useEffect(() => {
    api.get<FonctionRH[]>('/rh/fonctions').then(setFonctions).catch(() => {});
    api.get<Prestation[]>('/prestations').then(setPrestations).catch(() => {});
  }, []);

  const scenariosActifs = useMemo(
    () => (grille?.scenarios ?? []).filter((s) => !s.archive),
    [grille]
  );
  const variantes = useMemo(() => scenariosActifs.filter((s) => s.id !== socleId), [scenariosActifs, socleId]);
  const scenarioNom = scenarioId != null
    ? scenariosActifs.find((s) => s.id === scenarioId)?.nom ?? null
    : null;

  if (chargement) {
    return <div className="flex items-center justify-center" style={{ height: 240 }}><div className="spinner" /></div>;
  }

  const totaux = grille?.totaux;

  return (
    <div>
      <div className="page-header">
        <div className="flex items-center gap-8">
          <h1 className="page-title">RH — répartition du temps</h1>
          <span className="stat-pill">{fmtPct(totaux?.etpMoyen ?? 0)} ETP moyen</span>
          {scenarioId == null
            ? <span className="stat-pill" style={{ color: 'var(--ink-2)' }}
                title="Décisions arrêtées seulement — les mois à venir n'y sont qu'un report">décidé seulement</span>
            : scenarioId === socleId
              ? <span className="stat-pill" style={{ color: 'var(--accent)' }}
                  title="Le budget en l'état : décisions arrêtées plus hypothèses retenues">budget</span>
              : <span className="stat-pill" style={{ color: 'var(--warn)' }}>variante · {scenarioNom}</span>}
        </div>
        <div className="flex gap-8 items-center">
          <SelectAnnee annee={annee} onChange={setAnnee} />
          <select className="sort-select" value={moisRef} onChange={(e) => setMoisRef(Number(e.target.value))}
            title="Mois de référence des répartitions affichées">
            {MOIS_LABELS_LONGS.map((label, i) => (
              <option key={label} value={i + 1}>{label}</option>
            ))}
          </select>
        </div>
      </div>

      {erreur && <div className="card mb-16 p-16 text-err">{erreur}</div>}

      <div className="kpi-row mb-16">
        <div className="kpi-card">
          <span className="kpi-card__label">ETP moyen {annee}</span>
          <span className="kpi-card__value">{(totaux?.etpMoyen ?? 0).toFixed(2)}</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-card__label">Masse salariale {annee}</span>
          <span className="kpi-card__value">{fmtCHF(totaux?.coutAnnuel ?? 0)}<span className="kpi-card__suffix">CHF</span></span>
        </div>
        <div className="kpi-card">
          <span className="kpi-card__label">Sans répartition</span>
          <span className="kpi-card__value"
            style={{ color: (totaux?.nbSansRepartition ?? 0) > 0 ? 'var(--warn)' : undefined }}>
            {totaux?.nbSansRepartition ?? 0}
          </span>
        </div>
        <div className="kpi-card">
          <span className="kpi-card__label">Écarts à réconcilier ({MOIS_LABELS[moisRef - 1].toLowerCase()})</span>
          <span className="kpi-card__value"
            style={{ color: (totaux?.nbAvecEcart ?? 0) > 0 ? 'var(--err)' : undefined }}>
            {totaux?.nbAvecEcart ?? 0}
          </span>
        </div>
      </div>

      <div className="toolbar">
        <div className="selectable-group" title="Lecture par personne, par fonction (métier) ou par prestation (analytique)">
          <button className={`selectable${vue === 'personnes' ? ' active' : ''}`} onClick={() => setVue('personnes')}>Personnes</button>
          <button className={`selectable${vue === 'fonctions' ? ' active' : ''}`} onClick={() => setVue('fonctions')}>Par fonction</button>
          <button className={`selectable${vue === 'prestations' ? ' active' : ''}`} onClick={() => setVue('prestations')}>Par prestation</button>
        </div>
        <div className="flex gap-8 items-center" title="Décisions prises seules, ou avec les hypothèses d'un scénario">
          <div className="selectable-group">
            <button
              className={`selectable${scenarioId != null && scenarioId === socleId ? ' active' : ''}`}
              disabled={socleId == null}
              title="Le budget en l'état : décisions arrêtées plus hypothèses retenues"
              onClick={() => setScenarioId(socleId ?? null)}
            >
              Budget
            </button>
            <button
              className={`selectable${scenarioId != null && scenarioId !== socleId ? ' active' : ''}`}
              disabled={variantes.length === 0}
              title={variantes.length === 0 ? 'Aucune variante — en créer une dans Scénarios' : 'Une variante du budget'}
              onClick={() => setScenarioId(variantes[0]?.id ?? null)}
            >
              Variante
            </button>
            <button
              className={`selectable${scenarioId == null ? ' active' : ''}`}
              title="Décisions arrêtées seulement, sans aucune hypothèse — les mois à venir n'y sont qu'un report"
              onClick={() => setScenarioId(null)}
            >
              Décidé seulement
            </button>
          </div>
          {scenarioId != null && scenarioId !== socleId && (
            <select className="sort-select" value={scenarioId} onChange={(e) => setScenarioId(Number(e.target.value))}>
              {variantes.map((s) => <option key={s.id} value={s.id}>{s.nom}</option>)}
            </select>
          )}
          <button className="btn btn-icon" title="Gérer les scénarios"
            onClick={() => { window.location.hash = 'scenarios'; }}>
            <Settings2 className="icon-sm" />
          </button>
        </div>
      </div>

      {vue === 'personnes' && (
        <table className="table table-compact">
          <thead>
            <tr>
              <th>Personne</th>
              <th className="text-right">Taux {MOIS_LABELS[moisRef - 1].toLowerCase()}</th>
              <th className="text-right">Réparti</th>
              <th>Répartition en vigueur</th>
              <th className="text-right">Coût {annee}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {(grille?.employes ?? []).map((e) => {
              const ref = e.mois[moisRef - 1];
              return (
                <tr key={e.id} style={{ cursor: 'pointer' }} onClick={() => setFiche(e)}>
                  <td>
                    <span className="nom-employe">{e.nom}</span>
                    <span className="text-xs text-ink-3"> · {e.categorie}</span>
                  </td>
                  <td className="tabular-nums text-right">{fmtPct(ref.taux)}</td>
                  <td className="tabular-nums text-right">
                    {fmtPct(ref.somme)}
                    {Math.abs(ref.ecart) > 0.0001 && (
                      <span className="badge badge-err badge-sm" style={{ marginLeft: 6 }}
                        title={`La somme des lignes (${fmtPct(ref.somme)}) ne correspond pas au taux d'occupation (${fmtPct(ref.taux)})`}>
                        écart {fmtPct(ref.ecart)}
                      </span>
                    )}
                  </td>
                  <td className="text-sm">
                    {ref.lignes.length === 0
                      ? <span className="text-ink-3">— aucune répartition —</span>
                      : ref.lignes.map((l, i) => (
                          <span key={i} className="badge badge-sm" style={{ marginRight: 4 }}
                            title={l.prestationLibelle ? `${l.fonction} → ${l.prestationLibelle}` : l.fonction}>
                            {l.prestationLibelle ?? l.fonction} {fmtPct(l.taux)}
                          </span>
                        ))}
                  </td>
                  <td className="tabular-nums text-right">{fmtCHF(e.coutAnnuel)}</td>
                  <td>{ref.tag && <BadgeTagRevenu tag={ref.tag} scenarioNom={scenarioNom} />}</td>
                </tr>
              );
            })}
            {(grille?.employes ?? []).length === 0 && (
              <tr><td colSpan={6} className="text-ink-3">Aucune personne.</td></tr>
            )}
          </tbody>
        </table>
      )}

      {vue !== 'personnes' && (
        <TableauAgregats
          lignes={vue === 'fonctions' ? (grille?.fonctions ?? []) : (grille?.prestations ?? [])}
          nonReparti={grille?.nonReparti}
          intitule={vue === 'fonctions' ? 'Fonction' : 'Prestation'}
          annee={annee}
        />
      )}

      <div className="mt-16 text-xs text-ink-3">
        <p>
          La répartition se saisit ici, plus dans la fiche salariale : Salaires dit ce que coûte une
          personne, RH dit à quoi va son temps. Le coût d'une fonction ou d'une prestation est le salaire du
          mois ventilé au prorata des lignes déclarées.
        </p>
        <p>
          Les fonctions transversales (Direction, Finances, IT, Secrétariat…) restent sans prestation : leur
          coût est un coût de structure, à couvrir par la marge des prestations.
        </p>
      </div>

      {fiche && (
        <RepartitionModal
          employe={fiche}
          annee={annee}
          moisRef={moisRef}
          fonctions={fonctions}
          prestations={prestations.filter((p) => p.actif)}
          scenarios={grille?.scenarios ?? []}
          scenarioCourant={scenarioId ?? null}
          onFermer={() => setFiche(null)}
          onModifie={() => { setFiche(null); charger(); }}
        />
      )}
    </div>
  );
}

function TableauAgregats({ lignes, nonReparti, intitule, annee }: {
  lignes: AgregatRH[];
  nonReparti: AgregatRH | undefined;
  intitule: string;
  annee: number;
}) {
  const totalEtp = lignes.reduce((s, l) => s + l.etpMoyen, 0);
  const totalCout = lignes.reduce((s, l) => s + l.coutAnnuel, 0);

  return (
    <>
      <table className="table table-compact">
        <thead>
          <tr>
            <th>{intitule}</th>
            <th>Groupe</th>
            <th className="text-right">ETP moyen</th>
            <th className="text-right">Coût {annee}</th>
            <th className="text-right">Part</th>
          </tr>
        </thead>
        <tbody>
          {lignes.map((l) => (
            <tr key={`${l.id ?? 'transversal'}-${l.libelle}`}>
              <td className="font-semi">{l.libelle}</td>
              <td className="text-sm text-ink-3">{l.groupe ?? '—'}</td>
              <td className="tabular-nums text-right">{l.etpMoyen.toFixed(2)}</td>
              <td className="tabular-nums text-right">{fmtCHF(l.coutAnnuel)}</td>
              <td className="tabular-nums text-right">{fmtPct(l.partCout)}</td>
            </tr>
          ))}
          {lignes.length === 0 && (
            <tr><td colSpan={5} className="text-ink-3">
              Aucune répartition saisie — ouvrir une personne dans la vue « Personnes » pour commencer.
            </td></tr>
          )}
          <tr className="ligne-totaux">
            <td>Total réparti</td>
            <td />
            <td className="tabular-nums text-right">{totalEtp.toFixed(2)}</td>
            <td className="tabular-nums text-right">{fmtCHF(totalCout)}</td>
            <td />
          </tr>
        </tbody>
      </table>
      {nonReparti && nonReparti.coutAnnuel !== 0 && (
        <div className="card mt-16 p-16">
          <div className="flex items-center gap-8">
            <AlertTriangle className="icon-sm" style={{ color: 'var(--warn)' }} />
            <span className="font-semi">Temps non réparti</span>
          </div>
          <p className="text-sm text-ink-2 mt-8">
            {nonReparti.etpMoyen.toFixed(2)} ETP moyen et CHF {fmtCHF(nonReparti.coutAnnuel)} de salaires
            n'ont aucune répartition déclarée. Ce montant n'est jamais ventilé d'office : il reste visible ici
            jusqu'à ce que la répartition soit saisie.
          </p>
        </div>
      )}
    </>
  );
}

// ── Saisie de la répartition d'une personne ────────────────────────────────

interface LigneSaisie {
  categorieId: number;
  prestationId: number | null;
  tauxPct: string;
}

function RepartitionModal({
  employe, annee, moisRef, fonctions, prestations, scenarios, scenarioCourant, onFermer, onModifie,
}: {
  employe: LigneEmployeRH;
  annee: number;
  moisRef: number;
  fonctions: FonctionRH[];
  prestations: Prestation[];
  scenarios: ScenarioBudget[];
  scenarioCourant: number | null;
  onFermer: () => void;
  onModifie: () => void;
}) {
  const refMois = employe.mois[moisRef - 1];
  const [moisEffet, setMoisEffet] = useState(`${annee}-${String(moisRef).padStart(2, '0')}`);
  const [lignes, setLignes] = useState<LigneSaisie[]>(
    refMois.lignes.length > 0
      ? refMois.lignes.map((l) => ({
          categorieId: l.categorieId,
          prestationId: l.prestationId,
          tauxPct: String(Math.round(l.taux * 1000) / 10),
        }))
      : [{ categorieId: fonctions[0]?.id ?? 0, prestationId: null,
           tauxPct: String(Math.round(refMois.taux * 1000) / 10) }]
  );
  const [tag, setTag] = useState<'definitif' | 'projete'>(scenarioCourant != null ? 'projete' : 'definitif');
  const [scenarioId, setScenarioId] = useState<number | null>(scenarioCourant);
  const [commentaire, setCommentaire] = useState('');
  const [historique, setHistorique] = useState<EvenementRepartitionRH[] | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);
  // Comme ailleurs dans l'app : la valeur en vigueur est figée, on déverrouille
  // pour corriger (l'événement reste tracé par son commentaire).
  const [deverrouille, setDeverrouille] = useState(refMois.lignes.length === 0);

  const chargerHistorique = useCallback(() => {
    api.get<EvenementRepartitionRH[]>(`/rh/employes/${employe.id}/repartitions`)
      .then(setHistorique)
      .catch((e) => setErreur(e.message));
  }, [employe.id]);

  useEffect(() => { chargerHistorique(); }, [chargerHistorique]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onFermer(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onFermer]);

  const nomFonction = (id: number) => {
    const f = fonctions.find((x) => x.id === id);
    return f ? (f.parentNom ? `${f.parentNom} — ${f.nom}` : f.nom) : `#${id}`;
  };
  const nomScenario = (id: number | null) =>
    id == null ? null : scenarios.find((s) => s.id === id)?.nom ?? `#${id}`;

  const somme = lignes.reduce((s, l) => s + (Number(l.tauxPct) || 0) / 100, 0);
  const ecart = somme - refMois.taux;
  const valide =
    /^\d{4}-\d{2}$/.test(moisEffet) &&
    lignes.length > 0 &&
    lignes.every((l) => l.categorieId > 0 && Number(l.tauxPct) >= 0) &&
    commentaire.trim().length > 0 &&
    (tag === 'definitif' || scenarioId != null);

  async function enregistrer() {
    if (!valide || enCours) return;
    setEnCours(true);
    setErreur(null);
    try {
      await api.post(`/rh/employes/${employe.id}/repartitions`, {
        moisEffet,
        tag,
        ...(tag === 'projete' ? { scenarioId } : {}),
        commentaire: commentaire.trim(),
        lignes: lignes.map((l) => ({
          categorieId: l.categorieId,
          prestationId: l.prestationId,
          taux: Number(l.tauxPct) / 100,
        })),
      });
      onModifie();
    } catch (e) {
      setErreur((e as Error).message);
      setEnCours(false);
    }
  }

  async function supprimer(id: number) {
    const c = window.prompt('Commentaire de suppression (obligatoire) :');
    if (!c || !c.trim()) return;
    setErreur(null);
    try {
      await api.delete(`/rh/repartitions/${id}`, { commentaire: c.trim() });
      chargerHistorique();
      onModifie();
    } catch (e) {
      setErreur((e as Error).message);
    }
  }

  return (
    <div className="modal-overlay" onClick={onFermer}>
      <div className="modal" style={{ maxWidth: 760 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <div className="modal-title">{employe.nom}</div>
            <div className="text-xs text-ink-3">
              {employe.categorie} · taux {fmtPct(refMois.taux)} en {MOIS_LABELS_LONGS[moisRef - 1]} {annee}
              {refMois.moisEffet ? ` · répartition en vigueur depuis ${fmtMoisFr(refMois.moisEffet)}` : ''}
            </div>
          </div>
          <button className="close-btn" onClick={onFermer} title="Fermer (Esc)"><X className="icon-sm" /></button>
        </div>
        <div className="modal-body" style={{ overflowY: 'auto' }}>
          {!deverrouille ? (
            <div className="formulaire-changement mb-16">
              <div className="flex flex-col gap-4">
                {refMois.lignes.map((l, i) => (
                  <div key={i} className="flex items-center gap-8 text-sm">
                    <span style={{ minWidth: 240 }}>{l.fonction}</span>
                    <span className="text-ink-3" style={{ minWidth: 200 }}>
                      {l.prestationLibelle ?? '— transversal —'}
                    </span>
                    <strong className="tabular-nums">{fmtPct(l.taux)}</strong>
                  </div>
                ))}
                <div className="flex items-center gap-8 text-sm mt-8">
                  <span style={{ minWidth: 240 }}>Somme</span>
                  <span className="text-ink-3" style={{ minWidth: 200 }} />
                  <strong className="tabular-nums">{fmtPct(refMois.somme)}</strong>
                  {Math.abs(refMois.ecart) > 0.0001 && (
                    <span className="badge badge-err badge-sm">écart {fmtPct(refMois.ecart)} vs taux</span>
                  )}
                  {refMois.tag && <BadgeTagRevenu tag={refMois.tag} scenarioNom={nomScenario(scenarioCourant)} />}
                </div>
              </div>
              <div className="flex items-center justify-between mt-8">
                <span className="text-xs text-ink-3">Valeur figée — corriger crée un nouvel événement tracé.</span>
                <button className="btn btn-accent btn-sm" onClick={() => setDeverrouille(true)}>Corriger ✎</button>
              </div>
            </div>
          ) : (
            <div className="formulaire-changement mb-16">
              <div className="formulaire-changement__titre">
                Répartition applicable à partir d'un mois donné
              </div>
              <div className="flex gap-12 flex-wrap items-end mb-8">
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">À partir de</label>
                  <input type="month" className="form-input" value={moisEffet}
                    onChange={(e) => setMoisEffet(e.target.value)} />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Statut</label>
                  <ChoixTagScenario tag={tag} scenarioId={scenarioId} scenarios={scenarios}
                    onChange={(t, s) => { setTag(t); setScenarioId(s); }} />
                </div>
              </div>

              <table className="table table-compact">
                <thead>
                  <tr>
                    <th>Fonction</th>
                    <th>Prestation <span className="text-ink-3">(fonctions de terrain)</span></th>
                    <th className="text-right">Part du temps</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {lignes.map((l, i) => (
                    <tr key={i}>
                      <td>
                        <select className="form-input w-full" value={l.categorieId}
                          onChange={(e) => setLignes(lignes.map((x, j) =>
                            j === i ? { ...x, categorieId: Number(e.target.value) } : x))}>
                          {fonctions.map((f) => (
                            <option key={f.id} value={f.id}>
                              {f.parentNom ? `${f.parentNom} — ${f.nom}` : f.nom}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <select className="form-input w-full" value={l.prestationId ?? ''}
                          onChange={(e) => setLignes(lignes.map((x, j) =>
                            j === i ? { ...x, prestationId: e.target.value ? Number(e.target.value) : null } : x))}>
                          <option value="">— aucune (transversal) —</option>
                          {prestations.map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.groupe ? `${p.groupe} — ${p.libelle}` : p.libelle}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <input type="number" className="form-input" style={{ width: 90 }} min={0} step={1}
                          value={l.tauxPct}
                          onChange={(e) => setLignes(lignes.map((x, j) =>
                            j === i ? { ...x, tauxPct: e.target.value } : x))}
                          onFocus={(e) => e.target.select()} />
                        <span className="text-xs text-ink-3"> %</span>
                      </td>
                      <td>
                        <button className="btn btn-icon" title="Retirer la ligne"
                          onClick={() => setLignes(lignes.filter((_, j) => j !== i))}>
                          <Trash2 className="icon-sm" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="flex items-center justify-between mt-8">
                <button className="btn btn-sm"
                  onClick={() => setLignes([...lignes,
                    { categorieId: fonctions[0]?.id ?? 0, prestationId: null, tauxPct: '0' }])}>
                  <Plus className="icon-sm" /> Ajouter une ligne
                </button>
                <div className="flex items-center gap-8 text-sm">
                  <span>Somme : <strong className="tabular-nums">{fmtPct(somme)}</strong></span>
                  <span className="text-ink-3">taux d'occupation {fmtPct(refMois.taux)}</span>
                  {Math.abs(ecart) > 0.0001 && (
                    <span className="badge badge-err badge-sm"
                      title="L'écart est enregistré tel quel et signalé dans la grille — il n'est jamais corrigé en silence.">
                      écart {fmtPct(ecart)}
                    </span>
                  )}
                </div>
              </div>

              <div className="form-group mt-8">
                <label className="form-label">Commentaire (obligatoire)</label>
                <input className="form-input w-full" value={commentaire}
                  placeholder="Motif de la répartition ou de son changement…"
                  onChange={(e) => setCommentaire(e.target.value)} />
              </div>
              <div className="flex justify-end">
                <button className="btn btn-accent" disabled={!valide || enCours} onClick={enregistrer}>
                  {enCours ? 'Enregistrement…' : 'Enregistrer'}
                </button>
              </div>
            </div>
          )}

          {erreur && <div className="text-err text-sm mb-8">{erreur}</div>}

          <div className="text-xs text-ink-3">Historique des répartitions</div>
          <table className="table table-compact">
            <thead>
              <tr><th>Dès</th><th>Lignes</th><th>Statut</th><th>Commentaire</th><th /></tr>
            </thead>
            <tbody>
              {(historique ?? []).map((e) => (
                <tr key={e.id}>
                  <td className="tabular-nums">{fmtMoisFr(e.mois_effet)}</td>
                  <td className="text-sm">
                    {e.lignes.map((l) => (
                      <span key={l.id} className="badge badge-sm" style={{ marginRight: 4 }}>
                        {nomFonction(l.categorieId)}
                        {l.prestationId != null && ` → ${prestations.find((p) => p.id === l.prestationId)?.libelle ?? `#${l.prestationId}`}`}
                        {' '}{fmtPct(Number(l.taux))}
                      </span>
                    ))}
                  </td>
                  <td><BadgeTagRevenu tag={e.tag} scenarioNom={nomScenario(e.scenario_id)} /></td>
                  <td className="text-sm text-ink-2">
                    {e.commentaire}
                    {e.auteur && <span className="text-xs text-ink-3"> · {e.auteur}</span>}
                  </td>
                  <td>
                    <button className="btn btn-icon" title="Supprimer cet événement"
                      onClick={() => supprimer(e.id)}>
                      <Trash2 className="icon-sm" />
                    </button>
                  </td>
                </tr>
              ))}
              {(historique ?? []).length === 0 && (
                <tr><td colSpan={5} className="text-ink-3">Aucune répartition enregistrée.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
